import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import chatFavouriteService from "../../../services/chatFavouriteService";

// Async thunk to fetch favorite sessions
export const fetchFavouriteSessions = createAsyncThunk(
  "chatFavourites/fetchFavouriteSessions",
  async (args: { page: number; limit: number }, { rejectWithValue }) => {
    try {
      const response = await chatFavouriteService.getFavouriteSessions(args);
      return { data: response.data.data, params: response.params };
    } catch (error: any) {
      return rejectWithValue(error?.response.data || "Failed to fetch data");
    }
  }
);

// Async thunk to update favorite status
export const updateFavouriteStatus = createAsyncThunk(
  "chatFavourites/updateFavouriteStatus",
  async (
    { sessionId, isFavourite }: { sessionId: string; isFavourite: number },
    { rejectWithValue }
  ) => {
    try {
      const data = await chatFavouriteService.updateFavouriteStatus(
        sessionId,
        isFavourite
      );
      return { sessionId, isFavourite, data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to update status");
    }
  }
);

export const unFavouriteChat = createAsyncThunk(
  "chatFavourites/unFavouriteChat",
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const data = await chatFavouriteService.updateFavouriteStatus(
        sessionId,
        0 // 0 indicates unfavourite
      );
      return { sessionId, data };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to unfavorite all"
      );
    }
  }
);

export const unFavouriteAll = createAsyncThunk(
  "chatFavourites/unFavouriteAll",
  async (_, { rejectWithValue }) => {
    try {
      const data = await chatFavouriteService.unFavouriteAllSessions();
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to unfavorite all"
      );
    }
  }
);
type FavouriteSession = {
  session_id: string;
  session_name: string;
  is_favourite: boolean;
  updated_date: string;
};
type FavouriteSessions = {
  chat_id: string;
  session_name: string;
  updated_date: string;
};
const initialState = {
  favouriteSessions: [] as FavouriteSessions[],
  isFavouriteloading: false,
  isFavouriteError: false,
  favoriteSession: {} as FavouriteSession,
  isFavouritesUpdated: false,
  isEndOfFavouriteData: false,
  isFetchingMore: false, // Optional property to track if more data is being fetched
};

// Slice
const chatFavoriteSlice = createSlice({
  name: "chatFavourites",
  initialState,
  reducers: {
    addNewFavouriteSession: (state, action) => {
      state.favouriteSessions.unshift(action.payload);
    },
    removeFavoriteSession: (state, action) => {
      const sessionId = action.payload;
      state.favouriteSessions = state.favouriteSessions.filter(
        (session: FavouriteSessions) => session.chat_id !== sessionId
      );
    },
    setFavouriteSession: (state, action) => {
      state.favoriteSession = action.payload;
    },
    unFavouriteAllSessions: (state) => {
      state.favouriteSessions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFavouriteSessions.pending, (state, action) => {
        if (action.meta.arg.page === 1) {
          state.favouriteSessions = [];
          state.isFavouriteloading = true;
          state.isFavouriteError = false;
        }

        if (action.meta.arg.page > 1) {
          state.isFetchingMore = true; // Set to true when fetching more data
        }
      })
      .addCase(fetchFavouriteSessions.fulfilled, (state, action) => {
        state.isFavouriteloading = false;
        state.isFavouriteError = false;

        if (action.meta.arg.page === 1 && action.payload.data?.length === 0) {
          state.favouriteSessions = [];
        } else {
          state.isFetchingMore = false; // Reset fetching more state
          if (action.payload.data) {
            state.favouriteSessions.push(...action.payload.data); // Append new data to existing data
          } else {
            state.isEndOfFavouriteData = true; // Set to true if no more data is available
          }
        }
      })
      .addCase(fetchFavouriteSessions.rejected, (state, action) => {
        state.isFavouriteloading = false;
        state.isFavouriteError = true;
      })
      .addCase(updateFavouriteStatus.pending, (state) => {
        state.isFavouriteloading = true;
        state.isFavouriteError = false;
        state.isFavouritesUpdated = false;
      })
      .addCase(updateFavouriteStatus.fulfilled, (state, action) => {
        state.isFavouriteloading = false;
      })
      .addCase(updateFavouriteStatus.rejected, (state, action) => {
        state.isFavouriteloading = false;
        state.isFavouriteError = true;
        state.isFavouritesUpdated = false;
      });
    builder
      .addCase(unFavouriteChat.pending, (state) => {
        state.isFavouriteloading = true;
        state.isFavouriteError = false;
      })
      .addCase(unFavouriteChat.fulfilled, (state, action) => {
        state.isFavouriteloading = false;
        state.isFavouriteError = false;
        state.favouriteSessions = state.favouriteSessions.filter(
          (session: FavouriteSessions) =>
            session.chat_id !== action.payload.sessionId
        );
      })
      .addCase(unFavouriteChat.rejected, (state, action) => {
        state.isFavouriteloading = false;
        state.isFavouriteError = true;
      });
    builder
      .addCase(unFavouriteAll.pending, (state) => {
        state.isFavouriteloading = true;
        state.isFavouriteError = false;
      })
      .addCase(unFavouriteAll.fulfilled, (state) => {
        state.isFavouriteloading = false;
        state.isFavouriteError = false;
        state.favouriteSessions = [];
      })
      .addCase(unFavouriteAll.rejected, (state, action) => {
        state.isFavouriteloading = false;
        state.isFavouriteError = true;
      });
  },
});

export const {
  addNewFavouriteSession,
  setFavouriteSession,
  removeFavoriteSession,
  unFavouriteAllSessions,
} = chatFavoriteSlice.actions;

export default chatFavoriteSlice.reducer;
